/**
 * Định dạng ngày/giờ/số/sắp xếp — CLAUDE.md §8: luôn dùng `Intl` với locale,
 * không tự ghép chuỗi.
 *
 * Lý do không phải thẩm mỹ: `dd/mm/yyyy` cứng thì bản tiếng Anh hiện sai thứ
 * tự ngày-tháng, và `toLocaleString()` không truyền locale sẽ lấy locale của
 * MÁY người dùng — nên công tắc VI/EN trên giao diện không đổi được định dạng
 * số và ngày, chỉ đổi được chữ.
 */
const dateTimeCache = new Map<string, Intl.DateTimeFormat>();
const numberCache = new Map<string, Intl.NumberFormat>();
const collatorCache = new Map<string, Intl.Collator>();

function dateTimeFormatter(locale: string): Intl.DateTimeFormat {
  let formatter = dateTimeCache.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });
    dateTimeCache.set(locale, formatter);
  }
  return formatter;
}

/**
 * Chuỗi DATETIME từ MariaDB (`2026-08-09 14:30:00`) sang giờ địa phương.
 *
 * Driver được cấu hình `dateStrings: true` nên giá trị đến dưới dạng chuỗi
 * KHÔNG có múi giờ. `new Date('2026-08-09 14:30:00')` được trình duyệt hiểu là
 * giờ địa phương — đúng ý ở đây, vì DB đặt mốc bằng `CURRENT_TIMESTAMP` theo
 * giờ session. Thêm 'Z' vào cuối sẽ làm mọi mốc lệch đúng bằng offset múi giờ.
 */
export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return dateTimeFormatter(locale).format(date);
}

export function formatNumber(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  let formatter = numberCache.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    numberCache.set(locale, formatter);
  }
  return formatter.format(value);
}

export function formatPercent(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Số byte → "12,5 MB".
 *
 * Dùng `Intl.NumberFormat` với `style: 'unit'` để dấu thập phân và vị trí đơn
 * vị theo locale (CLAUDE.md §8) — tiếng Việt viết "12,5 MB", tiếng Anh "12.5 MB".
 * Bậc 1024 vì đây là kích thước file, không phải dung lượng đĩa quảng cáo.
 */
export function formatBytes(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value) || value < 0) return '—';

  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: units[unitIndex] ?? 'byte',
    unitDisplay: 'short',
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  }).format(size);
}

/**
 * Thời lượng giây → "1 giờ 5 phút".
 *
 * Không dùng `Intl.DurationFormat`: Safari chưa hỗ trợ. Thay bằng
 * `NumberFormat` với `style: 'unit'` cho từng thành phần — API này đòi `unit`
 * là bắt buộc, nên mỗi đơn vị cần một formatter riêng.
 */
export function formatDuration(seconds: number | null | undefined, locale: string): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  const unit = (value: number, name: 'hour' | 'minute' | 'second'): string =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'short' }).format(
      value,
    );

  const parts: string[] = [];
  if (hours > 0) parts.push(unit(hours, 'hour'));
  if (minutes > 0) parts.push(unit(minutes, 'minute'));
  if (parts.length === 0 || rest > 0) parts.push(unit(rest, 'second'));
  return parts.join(' ');
}

/**
 * So sánh chuỗi theo locale — dùng cho mọi phép sắp xếp hiển thị.
 *
 * `Array.sort()` trần so theo mã UTF-16, nên "Ánh" rơi xuống sau "Zoe" trong
 * danh sách tiếng Việt. `Intl.Collator` xếp đúng theo quy tắc chữ cái.
 */
export function localeCompare(locale: string): (a: string, b: string) => number {
  let collator = collatorCache.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
    collatorCache.set(locale, collator);
  }
  return collator.compare;
}

/** Tên ngôn ngữ theo locale hiện tại: 'en' → "Tiếng Anh" khi UI đang là vi. */
export function languageName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Tiêu đề bài giao về từ báo cáo là JSON `title_i18n`, không phải chuỗi thuần.
 *
 * Render thẳng sẽ hiện nguyên `{"vi":"Toán học"}` trước mặt người dùng. Backend
 * cố tình trả nguyên bản đồ đa ngữ để mỗi nơi tự chọn ngôn ngữ của mình —
 * `excel-export.ts` cũng có `pickTitle` cùng logic.
 */
export function pickTitle(titleJson: string | null, locale: string): string {
  if (!titleJson) return '—';
  let parsed: unknown;
  try {
    parsed = JSON.parse(titleJson);
  } catch {
    // Dữ liệu cũ có thể là chuỗi trần. Trả nguyên chứ không để trống ô.
    return titleJson;
  }
  if (typeof parsed === 'string') return parsed;
  if (typeof parsed !== 'object' || parsed === null) return '—';
  const map = parsed as Record<string, unknown>;
  const exact = map[locale];
  if (typeof exact === 'string') return exact;
  for (const value of Object.values(map)) {
    if (typeof value === 'string') return value;
  }
  return '—';
}
